/**
 * Balance-sheet document assembler — the whole report in one server-callable
 * function: org identity + native ledger read + closing rates + IFRS
 * translation + balance verdict, with nothing left for the template to derive.
 *
 * The screen (`useBalanceSheetDocument`) and the PDF route both call this one
 * function, so the PDF and the preview can't drift.
 */
import { buildDocumentMeta } from "../common.js";
import { previousFiscalYearEnd } from "../../lib/fiscalYear.js";
import { fetchOrgContext } from "../orgIdentity.js";
import { fetchBatchTranslationRates } from "../currencyTranslation.js";
import { fetchBalanceSheetNative } from "./fetch.js";
import { assembleTranslatedBalanceSheet } from "./translate.js";
import { balanceSheetBalanceCheck, } from "./model.js";
export async function assembleBalanceSheetDocument(client, { orgId, asOfDate, presentationCurrency, language, inlineLogo, now, onComparativeError, }) {
    // Read first: the org row carries the base currency both the presentation
    // default and the native fallback currency come from.
    const org = await fetchOrgContext(client, orgId, { inlineLogo });
    const resolvedPresentationCurrency = presentationCurrency ?? org.baseCurrency;
    /**
     * One period, end to end: ledger read → closing rates AT THAT PERIOD'S OWN
     * as-of date → IFRS translation → balance verdict.
     *
     * Extracted so the statutory prior-year column is drawn by the SAME pipeline
     * as the current one, rather than by a second, subtly different code path.
     * Everything it returns belongs to its own date: its rates are fetched at its
     * own closing date (IFRS own-closing-rate discipline), its foreign currencies
     * come from its own rows, and its balance verdict is its own.
     */
    const assemblePeriod = async (periodAsOfDate) => {
        // `baseCurrency` here is only the fallback for accounts with no currency set,
        // so it is the org's LEDGER currency — which is also what the screen passes
        // (it hands down its presentation currency, and on screen that is always
        // `selectedOrganization.currency`). Identical whenever the presentation
        // currency is the default; correct when it isn't.
        const native = await fetchBalanceSheetNative(client, {
            orgId,
            asOfDate: periodAsOfDate,
            // The result in equity splits at the start of the fiscal year holding
            // THIS period's date — the comparative column gets its own split.
            fiscalYearStartMonth: org.fiscalYearStartMonth,
            baseCurrency: org.baseCurrency,
        });
        // All account currencies from the RAW rows (before the zero-balance drop) —
        // the native assembler exposes them precisely so this layer never derives
        // currency presence from the filtered line items. Same list the hook builds.
        // Taken from THIS period's rows: an account funded in a currency the org no
        // longer holds still has to be translated in the column that shows it.
        const allCurrencies = native.data.accountCurrencies.map((c) => c.toUpperCase());
        const rates = await fetchBatchTranslationRates(client, {
            fromCurrencies: allCurrencies,
            toCurrency: resolvedPresentationCurrency,
            asOfDate: periodAsOfDate,
        });
        const translated = assembleTranslatedBalanceSheet({
            nativeData: native.data,
            rates: rates.rates,
            ratesArray: rates.ratesArray,
            presentationCurrency: resolvedPresentationCurrency,
            asOfDate: periodAsOfDate,
        });
        return {
            translated,
            ratesStatus: {
                allRatesFound: rates.allRatesFound,
                missingCurrencies: rates.missingCurrencies,
            },
            balanceCheck: balanceSheetBalanceCheck(translated),
        };
    };
    // The comparative column is the END OF THE PREVIOUS FINANCIAL YEAR (IAS
    // 34.20(a); RJ 394 by analogy) — not the same date a year earlier, which is
    // only a seasonal supplement. At a fiscal year-end the two coincide. The
    // document prints the date. The comparative's as-of read is therefore the
    // same day as the current column's prior-years read; reading it twice is
    // deliberate (a small per-account aggregate — not worth a shared cache).
    // Both periods run concurrently: wall-clock cost is the slower of the two.
    const priorAsOfDate = previousFiscalYearEnd(org.fiscalYearStartMonth, asOfDate);
    const [current, prior] = await Promise.all([
        assemblePeriod(asOfDate),
        // The catch sits INSIDE this member of the `Promise.all`, never around the
        // combinator: a rejection here must cost the document its prior column and
        // nothing else. Wrapping the `Promise.all` would let a transient prior-year
        // failure take the CURRENT period — the statement the reader actually asked
        // for — down with it, which is the doubled failure surface this guards.
        //
        // The whole prior pipeline is inside the closure (its ledger read, its rate
        // lookup, its translation and its balance verdict), so every step of it
        // degrades the same way instead of only the one that happened to be wrapped.
        assemblePeriod(priorAsOfDate).catch((error) => {
            onComparativeError?.(error);
            return null;
        }),
    ]);
    const { translated } = current;
    const comparative = prior
        ? {
            asOfDate: priorAsOfDate,
            translated: prior.translated,
            // Kept INDEPENDENT of the current period's, never merged: a rate missing
            // only for the prior date makes that column fall back to 1:1, which
            // mis-states it — the document has to warn about that column specifically
            // instead of branding a fully-rated current column unreliable (and the same
            // goes for a prior-only out-of-balance verdict).
            ratesStatus: prior.ratesStatus,
            balanceCheck: prior.balanceCheck,
        }
        : null;
    return {
        report: "balance-sheet",
        meta: buildDocumentMeta({
            orgId,
            orgTimezone: org.orgTimezone,
            baseCurrency: org.baseCurrency,
            language,
            now,
        }),
        org: org.identity,
        asOfDate,
        // Upper-cased by the translator; take it from there so the document and its
        // amounts can never disagree about the currency they're stated in.
        presentationCurrency: translated.presentationCurrency,
        translated,
        ratesStatus: current.ratesStatus,
        balanceCheck: current.balanceCheck,
        // Dated whether or not the column survived — the note that explains a lost
        // comparative has to say which period it lost.
        priorAsOfDate,
        comparativeStatus: comparative ? "included" : "unavailable",
        comparative,
    };
}
//# sourceMappingURL=assemble.js.map